import type { DrawContext } from './draw';
import type { View, Viewport } from './project';
import { toScreen } from './project';

/*
 * THE BACKDROP AND THE EDGE OF THE NAMING — the first layers of every frame.
 *
 * Ported from `reference/universe-v8/engine.js` — the head of `drawStatic` (1350–1387).
 *
 * Two things with one job between them: make the field feel like it has an outside. The backdrop
 * is one radial gradient over the whole page, brightest a little above centre. Past the rim there
 * is a halo where the naming stops, and it fades in as the reader pulls back — so the outside is
 * something they discover by zooming out rather than something always there.
 *
 * WHAT USED TO BE HERE, AND WHERE IT WENT. Theirs draws fifty-two clouds of deep dust beyond that
 * halo (engine.js 1388–1399): soft white ellipses, each turned by its own angle, squashed into the
 * world's plane and drifting at half the field's spin. It was ported faithfully and drawn here
 * until 2026-08-20, when Ariel asked for it to go — "remove those white washed skewed shapes
 * outside the most outer ring, it looks bad". `far-stars.ts` stands in its place and says why.
 *
 * That is a DELIBERATE DIVERGENCE from v8, and the only one in this file. The rest of it still
 * matches theirs call for call, and `void.test.ts` still asks their engine directly.
 *
 * THE HALO'S RADIUS IS A WORLD DIMENSION AND THEREFORE AN ARGUMENT. Theirs sits at 3,040, a number
 * that means nothing except in a world their size. The COLOURS are not data — they are the look,
 * and the look is the thing being ported.
 */

/** Their turn, in radians, exactly as written in the reference. */
const TAU = 6.2832;

/** Where the edge of the naming sits, and how the world is squashed. Both in world units. */
export interface VoidWorld {
  /** The radius the halo marking the end of the naming sits on. */
  haloRadius: number;
  /** The world's vertical scale. */
  squash: number;
}

/** The backdrop's three stops, and where its bright point sits. Look, not data. */
const BACKDROP = ['#0e0e17', '#0a0a11', '#070608'] as const;
const BACKDROP_MID = 0.55;
/** A little above centre, so the field reads as lit from where the eye already is. */
const BACKDROP_Y = 0.42;
const BACKDROP_REACH = 0.78;

/**
 * How much of the outside is showing: nothing when close in, all of it near the zoom floor.
 *
 * Measured against the FLOOR rather than against an absolute zoom, so it arrives when the reader has
 * pulled back as far as they can go — which is a different zoom for a reader who has seen more of the
 * field, and would otherwise never arrive at all for one who has seen a lot.
 */
export function voidFade(zoom: number, zoomFloor: number): number {
  return Math.min(1, Math.max(0, (zoomFloor * 2.6 - zoom) / (zoomFloor * 1.6)));
}

/** Below this the layer is not worth a single draw call. */
export const VOID_MIN_FADE = 0.02;

/** The whole-page wash the field sits on. Drawn in device pixels, before anything is projected. */
export function drawBackdrop(ctx: DrawContext, viewport: Viewport, dpr: number): void {
  const w = viewport.width;
  const h = viewport.height;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  const g = ctx.createRadialGradient(w / 2, h * BACKDROP_Y, 0, w / 2, h * BACKDROP_Y, Math.max(w, h) * BACKDROP_REACH);
  g.addColorStop(0, BACKDROP[0]);
  g.addColorStop(BACKDROP_MID, BACKDROP[1]);
  g.addColorStop(1, BACKDROP[2]);
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, w, h);
}

/** Below this on screen the halo is a smudge at the origin and is skipped. */
const HALO_MIN_PX = 50;

/**
 * The halo where the naming stops.
 *
 * A round gradient scaled into an ellipse, which is the same trick `neb` uses and for the same
 * reason: a radial gradient is round, so the space has to be squashed rather than the shape.
 */
function drawRimHalo(ctx: DrawContext, view: View, world: VoidWorld, fade: number): void {
  const centre = toScreen(view, 0, 0);
  const z = view.cam.z;
  const rx = world.haloRadius * z;
  if (rx <= HALO_MIN_PX) return;
  const ry = Math.max(0.001, world.haloRadius * world.squash * view.vt * z);
  ctx.save();
  ctx.translate(centre.x, centre.y);
  ctx.scale(1, ry / rx);
  const g = ctx.createRadialGradient(0, 0, rx * 0.9, 0, 0, rx * 1.55);
  g.addColorStop(0, 'rgba(118,144,196,0)');
  g.addColorStop(0.3, `rgba(124,150,200,${(0.055 * fade).toFixed(3)})`);
  g.addColorStop(1, 'rgba(88,110,158,0)');
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.arc(0, 0, rx * 1.55, 0, TAU);
  ctx.fill();
  ctx.restore();
}

/**
 * Everything past the rim — which, since the dust went, is the halo.
 *
 * Kept as its own entry point rather than folded into the caller: the fade, the guard and the
 * halo belong to one another, and `drawStatic` should ask for "beyond the rim" without knowing
 * what is currently out there.
 */
export function drawVoid(ctx: DrawContext, view: View, world: VoidWorld, fade: number): void {
  if (fade <= VOID_MIN_FADE) return;
  drawRimHalo(ctx, view, world, fade);
}
